import type { LevelEtaMode } from '@/core/display-settings'
import { performanceDiagnostics, type WidgetFailure, type WidgetRefreshReason } from './performance-diagnostics'
import { normalizeRateUnit, type RateUnit } from '@/core/display-settings'
import { rateLabel } from '@/i18n/display-settings'
import { widgetCopy } from '@/i18n/widget-copy'
import { h, render, type Component, type ComponentPublicInstance } from 'vue'
import { dailyPanelComponents } from '@/ui/daily-panel-components'
import orderStyles from '@/ui/order-widget.css?inline'
import dailyPanelStyles from '@/ui/daily-panel.css?inline'
import { dailyPanelBackground } from '@/ui/daily-panel'
import { createBuffComposition } from '@/buffs/composition'
import { getBuffOverlay, pulseBuffOverlay } from '@/buffs/overlay-source'
import { enabledBuffCount, type BuffSnapshot } from '@/buffs/types'
import type { StreamHudData } from '@/ui/stream-hud'
import buffStyles from '@/buffs/strip.css?inline'
import buffCompositionStyles from '@/buffs/composition.css?inline'
import { Clock3, Coins, Droplets, Gamepad2, HeartPulse, MapPinned, Pause, Play, Sparkles, Square } from 'lucide-vue-next'
import type { GameStateSnapshot, LiveRateSnapshot, MapNameConfirmationDiagnostic, SessionRecord } from '@/domain/types'
import type { AppLocale } from '@/i18n/messages'
import { GAME_ICONS } from '@/ui/game-icons'
import responsiveStyles from '@/ui/pip-responsive.css?inline'
import { defaultPipColors, isDailyPanelSkin, normalizePipSkin, type PipSkin } from '@/ui/pip-skins'
import { getPipDefaultSize, isStreamSkin, resolvePipBackground, type PipStreamOptions } from '@/ui/pip-skins'
import { buildWidgetSnapshot } from '@/ui/widget-snapshot'
import { buffPresentationKey, reuseWidgetData } from '@/ui/widget-presentation'
import { ExpRateTrendAnimator } from '@/ui/exp-rate-trend'
import { WidgetProgressAnimator } from '@/ui/widget-progress'
import expTrendStyles from '@/ui/exp-rate-trend.css?inline'
import { resetPipScale, fitPipScale } from '@/ui/pip-scale'
import { elapsedLayoutWidth, fitWidgetElapsed } from '@/ui/widget-elapsed'
import { allowsWidgetEllipsis } from '@/ui/widget-overflow'
import elapsedStyles from '@/ui/widget-elapsed.css?inline'
import { mountPipLoading } from '@/ui/pip-loading'
import ExpReadoutStatus from '@/components/ExpReadoutStatus.vue'
import ExpRateChart from '@/components/ExpRateChart.vue'
import WidgetPlayerIdentity from '@/components/WidgetPlayerIdentity.vue'
import expChartStyles from '@/ui/exp-rate-chart.css?inline'
import experienceStyles from '@/ui/pip-experience.css?inline'
import actionStyles from '@/ui/pip-actions.css?inline'
import { markPipResizeTutorialSeen, readPipResizeTutorialSeen, readPipWindowSize, savePipWindowSize } from './pip-window-size'
import { abandonDocumentPip, classifyPipError, getPipAvailability, isAbandonedDocumentPipError, PipOpenError, type PipFailureStage, type PipWidgetMode } from './pip-availability'

export type { PipSkin } from '@/ui/pip-skins'

export interface PipCallbacks {
  start(): void | Promise<void>
  pause(): void | Promise<void>
  resume(): void | Promise<void>
  end(): void | Promise<void>
  heartbeat?(): void
}

export interface PipData {
  theme?: 'light' | 'dark'
  rateUnit?: RateUnit
  levelEtaMode?: LevelEtaMode
  locale?: AppLocale
  mapRecognitionState?: MapNameConfirmationDiagnostic['state']
  skin?: PipSkin
  streamOptions?: PipStreamOptions
  levelUpId?: string
  session: SessionRecord | null
  state: GameStateSnapshot | null
  liveRates: LiveRateSnapshot | null
  canStart: boolean
  isStarting: boolean
  isEnding: boolean
  isPreparingForReload?: boolean
  enabledMetrics?: Readonly<{
    mesos: boolean
    hp: boolean
    mp: boolean
    mapName: boolean
  }>
}

interface PipLabels {
  elapsed: string
  exp: string
  mesos: string
  map: string
  mapPending: string
  start: string
  pause: string
  resume: string
  end: string
  ending: string
  starting: string
  hp: string
  mp: string
  level: string
  eta: string
  fullLevel: string
  gained: string
  startBalance: string
  balanceChange: string
  netChange: string
  metrics: string
}

export type PipLayout = 'full' | 'no-brand' | 'no-resources' | 'core' | 'exp-only' | 'micro'
export type PipShape = 'classic' | 'portrait' | 'strip'

export function resolvePipShape(width: number, height: number): PipShape {
  if (height >= 300 && height / width >= 1.5) return 'portrait'
  if (width >= 480 && height < 220 && width / height >= 2.5) return 'strip'
  return 'classic'
}

export function selectPipDetail(fits: (detail: number) => boolean, maximum = 5): number {
  for (let detail = Math.min(5, maximum); detail >= 0; detail -= 1) {
    if (fits(detail)) return detail
  }
  return 0
}

// Fixed tracks and overflow:hidden can conceal clipped descendants from the root.
export function pipContentFits(root: HTMLElement): boolean {
  const bounds = root.getBoundingClientRect()
  if (!bounds.width || !bounds.height) return true
  const range = root.ownerDocument.createRange()
  const tolerance = 1
  for (const node of root.querySelectorAll<HTMLElement>('*')) {
    if (node.closest('.exp-readout-dialog, .exp-readout-status.is-icon-only')) continue
    if (node.closest('.exp-rate-trend-layer, .exp-chart')) continue
    if (node.closest('.buff-rail') && !node.classList.contains('buff-rail')) continue
    if (!node.getClientRects().length || node.closest('svg, .bar, .location, .brand, .exp-aux')) continue
    const box = node.getBoundingClientRect()
    if (box.left < bounds.left - tolerance || box.right > bounds.right + tolerance
      || box.top < bounds.top - tolerance || box.bottom > bounds.bottom + tolerance) return false
    // ETA captions intentionally ellipsize; the full label is on their parent's
    // title. Keep measuring the estimate, but don't retire duration for a caption.
    if (node.matches('.experience-footer-eta > span')) continue
    // The stable badge box is measured; clipped animated layers cannot lower detail tiers.
    if (node.classList.contains('exp-rate-trend')) continue
    // Clipped profession/title text must not retire unrelated metrics.
    // Its box and vertical bounds still have to fit.
    const ellipsized = allowsWidgetEllipsis(node)
    if (!ellipsized && !node.classList.contains('buff-rail') && node.clientWidth && node.scrollWidth > node.clientWidth + tolerance) return false
    // Mixed fonts can extend past a text row's line box without exceeding the
    // widget. Enlarging the canvas cannot fix those font metrics. Still check
    // descendant boxes/text below, and scroll overflow of layout containers.
    const textRow = node.matches('.metric-gained, .exp-rate-group, .metric-value, .mesos-net, .meta-item, .elapsed, .metric-label, .metric-context, .status')
    if (!node.classList.contains('buff-rail') && !textRow && node.children.length && node.clientHeight && node.scrollHeight > node.clientHeight + tolerance) return false
    for (const child of node.childNodes) {
      if (child.nodeType !== 3 || !child.textContent?.trim()) continue
      range.selectNodeContents(child)
      const text = range.getBoundingClientRect()
      if ((!ellipsized && (text.left < box.left - tolerance || text.right > box.right + tolerance))
        || text.top < bounds.top - tolerance || text.bottom > bounds.bottom + tolerance) return false
    }
  }
  return true
}

export function resolvePipLayout(width: number, height: number): PipLayout {
  if (width < 12.5 * 16 || height < 7.25 * 16) return 'micro'
  if (width < 14.75 * 16 || height < 9 * 16) return 'exp-only'
  if (width < 16.25 * 16 || height < 11.25 * 16) return 'core'
  if (width < 18.75 * 16 || height < 13.75 * 16) return 'no-resources'
  if (width < 21.25 * 16 || height < 16.25 * 16) return 'no-brand'
  return 'full'
}

export class PipService {
  private pipWindow: Window | null = null
  private windowMode: PipWidgetMode | null = null
  private timer: number | null = null
  private heartbeatTimer: number | null = null
  private timerWindow: Window | null = null
  private layoutCleanup: (() => void) | null = null
  private sizeCleanup: (() => void) | null = null
  private refreshData: ((force?: boolean, reason?: WidgetRefreshReason) => void) | null = null
  private pendingRefresh: { owner: Window; id: number; frame: boolean } | null = null
  private rendererCleanup: (() => void) | null = null
  private pageCleanup: (() => void) | null = null
  private openerCleanup: (() => void) | null = null
  private loadingCleanup: (() => void) | null = null
  private opening: Promise<'opened' | 'cancelled'> | null = null
  private cancelOpening: (() => void) | null = null
  private generation = 0

  constructor(private readonly loadRenderer = async () => {
    const [renderer, styles] = await Promise.all([
      import('@/components/StreamHud.vue'), import('@/ui/stream-hud.css?inline'),
    ])
    return { component: renderer.default as Component, styles: styles.default }
  }) {}

  get supported(): boolean {
    return getPipAvailability().supported
  }

  open(
    getData: () => PipData,
    callbacks: PipCallbacks,
    locale: AppLocale,
    preferredMode: PipWidgetMode = 'pip',
  ): Promise<'opened' | 'cancelled'> {
    if (this.opening) return this.opening
    const availability = getPipAvailability(undefined, preferredMode)
    if (availability.supported && this.pipWindow && !this.pipWindow.closed && this.windowMode !== availability.mode) {
      const previous = this.pipWindow
      // Release the old display before creating the new cancellation scope. Recording lives in the store.
      this.close()
      try { previous.close() } catch { /* A stale window must not prevent reopening. */ }
    }
    const cancelled = new Promise<'cancelled'>(resolve => { this.cancelOpening = () => resolve('cancelled') })
    // openWindow reaches requestWindow synchronously, preserving user activation.
    const opening = Promise.race([this.openWindow(getData, callbacks, locale, this.generation, preferredMode), cancelled])
    this.opening = opening
    const settled = () => {
      if (this.opening !== opening) return
      this.opening = null
      this.cancelOpening = null
    }
    void opening.then(settled, settled)
    return opening
  }

  private async openWindow(
    getData: () => PipData, callbacks: PipCallbacks, locale: AppLocale, generation: number, preferredMode: PipWidgetMode,
  ): Promise<'opened' | 'cancelled'> {
    let pip: Window | undefined
    let stage: PipFailureStage = 'availability'
    try {
      const availability = getPipAvailability(undefined, preferredMode)
      if (availability.reason) throw new PipOpenError(availability.reason, stage)
      stage = 'request'
      if (this.pipWindow && !this.pipWindow.closed) {
        this.pipWindow.focus()
        return 'opened'
      }
      const skin = normalizePipSkin(getData().skin)
      const fallback = getPipDefaultSize(skin)
      const saved = readPipWindowSize(skin)
      const size = saved ?? fallback
      const opened = availability.mode === 'pip'
        ? await window.documentPictureInPicture!.requestWindow(skin === 'daily-order' ? { ...size, preferInitialWindowPlacement: true } : size)
        : this.openPopupWindow(size)
      pip = opened ?? undefined
      if (!pip) throw new PipOpenError('request-denied', 'request', 'NotAllowedError')
      if (generation !== this.generation || pip.closed) {
        if (!pip.closed) pip.close()
        return 'cancelled'
      }
      this.pipWindow = pip
      this.windowMode = availability.mode
      const onPageHide = () => { if (this.pipWindow === pip) this.close() }
      pip.addEventListener('pagehide', onPageHide, { once: true })
      this.pageCleanup = () => pip?.removeEventListener('pagehide', onPageHide)
      if (availability.mode === 'popup') this.watchPopupLifetime(pip, generation)
      this.trackWindowSize(pip, generation, getData)
      stage = 'initialization'
      this.loadingCleanup = mountPipLoading(pip.document, locale, document.documentElement.dataset.theme === 'dark')
      stage = 'resources'
      const renderer = await this.loadRenderer()
      if (generation !== this.generation || pip.closed || this.pipWindow !== pip) {
        if (this.pipWindow === pip) this.close()
        if (!pip.closed) pip.close()
        return 'cancelled'
      }
      stage = 'initialization'
      this.loadingCleanup?.()
      this.loadingCleanup = null
      this.initializeWindow(pip, getData, callbacks, locale, renderer.component, renderer.styles, undefined, undefined, true)
      return generation === this.generation && !pip.closed ? 'opened' : 'cancelled'
    } catch (error) {
      if (generation !== this.generation || pip?.closed) return 'cancelled'
      // Failure must reject rather than resolving the user-cancellation race.
      this.cancelOpening = null
      if (pip && this.pipWindow === pip) this.close()
      try { if (pip && !pip.closed) pip.close() } catch { /* best effort after teardown */ }
      const classified = classifyPipError(error, stage)
      if (preferredMode === 'pip' && isAbandonedDocumentPipError(classified)) abandonDocumentPip()
      throw classified
    }
  }

  private openPopupWindow(size: { width: number; height: number }): Window | null {
    return window.open('', 'maple-log-widget', `popup=yes,width=${Math.round(size.width)},height=${Math.round(size.height)}`)
  }

  private watchPopupLifetime(pip: Window, generation: number): void {
    const onOpenerHide = () => {
      if (this.generation !== generation || this.pipWindow !== pip) return
      try { if (!pip.closed) pip.close() } catch { /* opener teardown is best effort */ }
      this.close()
    }
    window.addEventListener('pagehide', onOpenerHide)
    const poll = window.setInterval(() => {
      if (this.generation !== generation || this.pipWindow !== pip) return
      if (pip.closed) this.close()
    }, 500)
    this.openerCleanup = () => {
      window.removeEventListener('pagehide', onOpenerHide)
      window.clearInterval(poll)
    }
  }

  private trackWindowSize(pip: Window, generation: number, getData: () => PipData): void {
    let previous = { width: pip.innerWidth, height: pip.innerHeight }
    const onResize = () => {
      if (pip.closed || this.pipWindow !== pip || this.generation !== generation) return
      const size = { width: pip.innerWidth, height: pip.innerHeight }
      if (size.width === previous.width && size.height === previous.height) return
      // Save immediately: a close or factory reset must not leave a pending write.
      if (savePipWindowSize(size, normalizePipSkin(getData().skin))) previous = size
    }
    pip.addEventListener('resize', onResize)
    this.sizeCleanup = () => pip.removeEventListener('resize', onResize)
  }

  async mountPreview(pip: Window, getData: () => PipData, locale: AppLocale, overlay: () => BuffSnapshot | null, sample: () => StreamHudData): Promise<void> {
    const generation = this.generation
    const renderer = await this.loadRenderer()
    if (generation !== this.generation) return
    this.pipWindow = pip
    this.initializeWindow(pip, getData, { start() {}, pause() {}, resume() {}, end() {} }, locale, renderer.component, renderer.styles, overlay, sample)
  }

  private initializeWindow(
    pip: Window, getData: () => PipData, callbacks: PipCallbacks, locale: AppLocale,
    StreamHud: Component, streamStyles: string,
    overlay: () => BuffSnapshot | null = getBuffOverlay, sample?: () => StreamHudData, showResizeTutorial = false,
  ): void {
    const generation = this.generation
    const productName = widgetCopy(locale, 'Maple Log', '枫记')
    const getLabels = (locale: AppLocale): PipLabels => widgetCopy(locale, { elapsed: 'Elapsed', exp: 'EXP / h', mesos: 'Mesos / h', map: 'Map', mapPending: 'Recognizing', start: 'Start', pause: 'Pause', resume: 'Resume', end: 'End', ending: 'Finishing', starting: 'Confirming level and EXP', hp: 'HP', mp: 'MP', level: 'Lv.', eta: 'Level up', fullLevel: 'Full level', gained: 'Gained', startBalance: 'Start balance', balanceChange: 'Change', netChange: 'Net', metrics: 'Live metrics' }, { elapsed: '记录时长', exp: 'EXP / 小时', mesos: '金币 / 小时', map: '地图', mapPending: '识别中', start: '开始记录', pause: '暂停', resume: '继续', end: '结束', ending: '正在结束', starting: '正在确认', hp: 'HP', mp: 'MP', level: '等级', eta: '距升级', fullLevel: '本级共需', gained: '本次', startBalance: '开始持有', balanceChange: '变化', netChange: '净', metrics: '实时数据' })
    let rateUnit = normalizeRateUnit(getData().rateUnit)
    const unitLabels = () => ({ ...getLabels(locale), exp: rateLabel(locale, rateUnit), mesos: rateLabel(locale, rateUnit, 'mesos') })
    let labels = unitLabels()
    pip.document.documentElement.lang = locale
    pip.document.title = productName

    let dark = (getData().theme ?? document.documentElement.dataset.theme) === 'dark'
    pip.document.documentElement.dataset.theme = dark ? 'dark' : 'light'
    const colors = defaultPipColors(dark)
    const style = pip.document.createElement('style')
    style.textContent = `
      :root{color-scheme:${dark ? 'dark' : 'light'};font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",Inter,"Microsoft YaHei",sans-serif;font-size:16px;letter-spacing:0;--pip-bg:${colors.bg};--pip-surface:${colors.surface};--pip-subtle:${colors.subtle};--pip-text:${colors.text};--pip-muted:${colors.muted};--pip-border:${colors.border};--pip-danger:${colors.danger};--pip-danger-soft:${colors.dangerSoft};--pip-success:${colors.green};--pip-success-soft:${colors.greenSoft};--pip-exp:${colors.exp};--pip-mesos:${colors.mesos};--pip-warning:${colors.amber};--pip-warning-soft:${colors.amberSoft};--pip-hp:${colors.hp};--pip-mp:${colors.mp};--pip-shadow:0 .125rem .375rem rgb(15 23 42 / ${dark ? '24%' : '5%'}),0 .0625rem .125rem rgb(15 23 42 / ${dark ? '16%' : '3%'});--pip-motion:160ms;--pip-ease:cubic-bezier(.16,1,.3,1);background:var(--pip-bg);color:var(--pip-text)}
      *{box-sizing:border-box}html,body{width:100%;height:100%;margin:0;overflow:hidden}body{background:var(--pip-bg);overscroll-behavior:none}button{font:inherit;touch-action:manipulation}[hidden]{display:none!important}
      @keyframes pip-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.45;transform:scale(.85)}}
      @keyframes resize-tutorial-frame{0%,100%{transform:scale(.92);opacity:.62}50%{transform:scale(1.04);opacity:1}}
      @keyframes resize-tutorial-arrow{0%,100%{transform:translate(0,0);opacity:.68}50%{transform:translate(.5rem,.5rem);opacity:1}}
      @keyframes resize-tutorial-handle{0%,100%{transform:scale(1);opacity:.72}50%{transform:scale(1.16);opacity:1}}
      @keyframes resize-tutorial-timeout{0%,92%{opacity:1}100%{opacity:0}}
      .widget{width:100%;height:100dvh;min-width:0;min-height:0;padding:.625rem;display:grid;grid-template-rows:auto auto minmax(0,1fr) auto;gap:.4375rem;overflow:hidden;background:var(--pip-bg)}
      .widget-header{min-width:0;min-height:2rem;display:flex;align-items:center;justify-content:space-between;gap:.625rem}.brand{min-width:0;display:flex;align-items:center;gap:.5rem}.brand-copy{min-width:0}.brand-copy strong{display:block;overflow:hidden;color:var(--pip-text);font-size:.875rem;line-height:1.15;font-weight:760;text-overflow:ellipsis;white-space:nowrap;letter-spacing:0}.header-meta{min-width:0;display:flex;align-items:center;justify-content:flex-end;gap:.5rem}.elapsed{min-width:0;display:inline-flex;align-items:center;gap:.25rem;padding:.1875rem .4375rem;border:1px solid var(--pip-border);border-radius:62.4375rem;background:var(--pip-surface);color:var(--pip-muted);font-size:.6875rem;line-height:1;font-variant-numeric:tabular-nums;white-space:nowrap;box-shadow:0 .0625rem .125rem rgb(15 23 42 / ${dark ? '16%' : '3%'})}.elapsed svg{width:.75rem;height:.75rem;color:var(--pip-muted)}.elapsed strong{color:var(--pip-text);font-weight:720}
      .brand-mark,.metric-icon{position:relative;display:grid;place-items:center;flex:none}.brand-mark{width:2rem;height:2rem}.metric-icon{width:1.625rem;height:1.625rem}.brand-mark>svg,.metric-icon>svg{width:68%;height:68%;color:var(--pip-muted)}.brand-mark img,.metric-icon img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;image-rendering:auto;filter:drop-shadow(0 .0625rem .125rem rgb(0 0 0 / 12%))}
      /* Reveal the fallback only after a failed image is removed. */
      .brand-mark:has(>img)>svg,.metric-icon:has(>img)>svg{visibility:hidden}
      .status{min-width:0;min-height:1.5rem;max-width:7.5rem;padding:0 .5rem;display:inline-flex;align-items:center;gap:.3125rem;overflow:hidden;border:1px solid var(--pip-border);border-radius:62.4375rem;background:var(--pip-subtle);color:var(--pip-muted);font-size:.6875rem;font-weight:720;white-space:nowrap}.status span{overflow:hidden;text-overflow:ellipsis}.status-dot{width:.375rem;height:.375rem;flex:none;border-radius:50%;background:currentColor}.status-running{border-color:color-mix(in srgb,var(--pip-success) 38%,var(--pip-border));color:var(--pip-success);background:var(--pip-success-soft)}.status-running .status-dot{animation:pip-pulse 2s cubic-bezier(.4,0,.6,1) infinite}.status-paused,.status-ending,.status-starting{border-color:color-mix(in srgb,var(--pip-warning) 38%,var(--pip-border));color:var(--pip-warning);background:var(--pip-warning-soft)}.status-interrupted{border-color:color-mix(in srgb,var(--pip-danger) 38%,var(--pip-border));color:var(--pip-danger);background:var(--pip-danger-soft)}
      .location{min-width:0;min-height:1.75rem;padding:0 .625rem;display:flex;align-items:center;gap:.375rem;border:1px solid var(--pip-border);border-radius:.5rem;background:var(--pip-surface);color:var(--pip-muted);box-shadow:0 .0625rem .125rem rgb(15 23 42 / ${dark ? '16%' : '3%'})}.location svg{width:.875rem;height:.875rem;flex:none;color:var(--pip-danger)}.location-label{flex:none;font-size:.6875rem;font-weight:650}.location strong{min-width:0;flex:1;overflow:hidden;color:var(--pip-text);font-size:.75rem;font-weight:700;text-align:left;text-overflow:ellipsis;white-space:nowrap}
      .data-panel{min-width:0;min-height:0;display:grid;grid-template-rows:minmax(0,1fr) auto;overflow:hidden;border:1px solid var(--pip-border);border-radius:.5rem;background:var(--pip-surface);box-shadow:var(--pip-shadow)}.core-metrics{min-width:0;min-height:0;display:grid;grid-template-columns:minmax(0,1.18fr) minmax(0,1fr)}.core-metrics.is-single-metric{grid-template-columns:minmax(0,1fr)}.metric{min-width:0;min-height:0;padding:.625rem .75rem;display:flex;flex-direction:column;justify-content:center;overflow:hidden}.metric+.metric{border-left:1px solid var(--pip-border)}.metric-heading{min-width:0;display:flex;align-items:center;justify-content:space-between;gap:.5rem}.metric-label{flex:0 1 auto;min-width:0;display:flex;align-items:center;gap:.4375rem;color:var(--pip-muted);font-size:.75rem;line-height:1;font-weight:700;white-space:nowrap}.metric-label>span:last-child{min-width:0;overflow:hidden;text-overflow:ellipsis}.metric-context{display:flex;flex:1 1 0;justify-content:flex-end;min-width:0;overflow:hidden;padding:.0625rem .3125rem;border-radius:62.4375rem;background:var(--pip-subtle);color:var(--pip-muted);font-size:.6875rem;line-height:1.2;font-weight:700;font-variant-numeric:tabular-nums;text-overflow:ellipsis;white-space:nowrap}.metric-value{min-height:1.5rem;margin-top:.3125rem;overflow:hidden;color:var(--pip-text);font-size:1.375rem;line-height:1;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:0;text-overflow:ellipsis;white-space:nowrap}.exp-metric .metric-value{color:var(--pip-exp)}.exp-value-row{display:flex;align-items:baseline;gap:.5rem;overflow:visible;text-overflow:clip}.exp-value-row>[data-exp-rate]{min-width:0;overflow:hidden;text-overflow:ellipsis}.exp-value-row .metric-gained{margin-left:auto}.metric-gained{min-width:0;display:inline-flex;align-items:baseline;gap:.1875rem;overflow:hidden;color:var(--pip-muted);font-size:.5625rem;line-height:1;letter-spacing:0}.metric-gained small{flex:none;font:inherit}.metric-gained strong{min-width:0;overflow:hidden;color:var(--pip-text);font-size:.6875rem;font-weight:750;text-overflow:ellipsis}.exp-metric .metric-gained strong{color:var(--pip-exp)}.mesos-metric .metric-value{color:var(--pip-mesos);font-size:1.25rem}.mesos-value-row{display:flex;align-items:baseline;gap:.375rem;overflow:visible;text-overflow:clip}.mesos-value-row>[data-mesos-rate]{min-width:0;overflow:hidden;text-overflow:ellipsis}.mesos-net{min-width:0;margin-left:auto;display:inline-flex;align-items:baseline;gap:.125rem;overflow:hidden;color:var(--pip-muted);font-size:.5625rem;line-height:1;letter-spacing:0;white-space:nowrap}.mesos-net small{flex:none;font:inherit}.mesos-net strong{min-width:0;overflow:hidden;color:var(--pip-text);font-size:.6875rem;font-weight:750;font-variant-numeric:tabular-nums;text-overflow:ellipsis}.metric-meta{min-width:0;margin-top:.375rem;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.5rem}.meta-item{min-width:0;display:flex;align-items:baseline;gap:.25rem;overflow:hidden;color:var(--pip-muted);font-size:.625rem;line-height:1.1;white-space:nowrap}.meta-item small{flex:none;font:inherit}.meta-item strong{min-width:0;overflow:hidden;color:var(--pip-text);font-size:.6875rem;font-weight:750;font-variant-numeric:tabular-nums;text-overflow:ellipsis}.meta-item:last-child{justify-content:flex-end}.mesos-meta{display:flex;flex-direction:column;align-items:stretch;gap:.25rem}.mesos-meta .mesos-balance-change{justify-content:flex-start}.signed-positive{color:var(--pip-danger)!important}.signed-negative{color:var(--pip-success)!important}
      .widget[data-layout="full"] .metric{padding-block:.5rem}.widget[data-layout="full"] .metric-value{margin-top:.5rem;line-height:1.08}.widget[data-layout="full"] .metric-meta{margin-top:.5rem}.widget[data-layout="full"] .exp-metric{padding-block:.25rem}.widget[data-layout="full"] .exp-metric .metric-value{margin-top:.375rem}.widget[data-layout="full"] .exp-metric .metric-meta{margin-top:.75rem}.widget[data-layout="full"] .meta-item{line-height:1.3}.widget[data-layout="full"] .resources{padding-block:.4375rem}
      .resources{min-width:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));padding:.5rem .75rem;border-top:1px solid var(--pip-border)}.resources.is-single-resource{grid-template-columns:minmax(0,1fr)}.resource{min-width:0;padding:0 .75rem}.resource.hp{padding-left:0;--resource-color:var(--pip-hp)}.resource.mp{padding-right:0;--resource-color:var(--pip-mp)}.resource+.resource{border-left:1px solid var(--pip-border)}.resources.is-single-resource .resource{padding-inline:0;border-left:0}.resource-heading{min-width:0;display:flex;align-items:center;justify-content:space-between;gap:.5rem}.resource-name{min-width:0;display:flex;align-items:center;gap:.375rem;color:var(--pip-muted);font-size:.6875rem;font-weight:740}.resource-name .metric-icon{width:1.25rem;height:1.25rem}.resource-percent{color:var(--pip-text);font-size:1rem;line-height:1;font-weight:800;font-variant-numeric:tabular-nums}.bar{height:.3125rem;margin-top:.375rem;overflow:hidden;border-radius:62.4375rem;background:color-mix(in srgb,var(--resource-color) 12%,var(--pip-subtle))}.bar>i{display:block;width:var(--value);height:100%;border-radius:inherit;background:var(--resource-color);transition:width 180ms var(--pip-ease)}
      .exp-metric .exp-aux{margin-inline:0;margin-bottom:0;grid-template-columns:repeat(2,minmax(0,1fr));gap:0;overflow:hidden}.exp-aux-item{min-width:0;margin:0;padding-right:.5rem;display:grid;align-content:start;gap:.3125rem;overflow:hidden}.exp-aux-item+.exp-aux-item{padding-right:0;padding-left:.5rem;border-left:1px solid var(--pip-border)}.exp-aux-item dt,.exp-aux-item dd{min-width:0;margin:0;white-space:nowrap}.exp-aux-item dt{color:var(--pip-muted);font-size:.5625rem;line-height:1;font-weight:500}.exp-aux-item dd{overflow:hidden;color:var(--pip-text);font-size:.6875rem;line-height:1.15;font-weight:750;font-variant-numeric:tabular-nums;text-overflow:ellipsis}
      .pip-display-failed{--pip-text:${colors.text};--pip-surface:${colors.surface};background:${colors.bg};color:${colors.text};display:flex!important;flex-direction:column;justify-content:center;padding:.5rem;overflow:auto!important}.pip-display-failed>:not(.widget-actions){display:none!important}.pip-display-failed>.widget-actions{width:100%;flex:none;grid-template-columns:repeat(2,minmax(0,1fr))}.pip-display-failed>.widget-actions button{min-height:2rem;white-space:normal}
      .widget-render-error{grid-column:1/-1;white-space:normal;font-size:.6875rem;line-height:1.4;color:var(--pip-text);background:var(--pip-surface);padding:.25rem .375rem;border-radius:.375rem}
      .resize-tutorial{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:1rem;overflow:hidden;color:var(--pip-text);background:rgb(4 8 14 / 72%);font-family:inherit;animation:resize-tutorial-timeout 8s linear forwards}
      .resize-tutorial-card{position:relative;z-index:2;width:min(22rem,calc(100% - 1rem));padding:1.25rem 1.25rem 1rem;border:1px solid color-mix(in srgb,var(--pip-exp) 52%,var(--pip-border));border-radius:.875rem;background:color-mix(in srgb,var(--pip-surface) 94%,#0b1220);box-shadow:0 .75rem 2rem rgb(0 0 0 / 36%),0 0 0 .1875rem color-mix(in srgb,var(--pip-exp) 14%,transparent);text-align:center}
      .resize-tutorial-visual{position:relative;width:8rem;height:4.25rem;margin:0 auto .75rem}
      .resize-tutorial-frame{position:absolute;inset:.25rem 1.1rem .25rem .25rem;border:2px solid var(--pip-exp);border-radius:.375rem;box-shadow:0 0 0 .1875rem color-mix(in srgb,var(--pip-exp) 18%,transparent);animation:resize-tutorial-frame 2.4s cubic-bezier(.4,0,.2,1) infinite}
      .resize-tutorial-frame:before,.resize-tutorial-frame:after{content:"";position:absolute;left:.5rem;right:.5rem;height:.1875rem;border-radius:99rem;background:color-mix(in srgb,var(--pip-text) 34%,transparent)}
      .resize-tutorial-frame:before{top:.625rem}.resize-tutorial-frame:after{top:1.125rem;right:1.25rem}
      .resize-tutorial-arrow{position:absolute;right:.25rem;bottom:.15rem;color:var(--pip-exp);font-size:2rem;line-height:1;animation:resize-tutorial-arrow 1.2s cubic-bezier(.4,0,.2,1) infinite}
      .resize-tutorial-handle{position:absolute;right:.1rem;bottom:0;width:1.25rem;height:1.25rem;border-right:3px solid var(--pip-exp);border-bottom:3px solid var(--pip-exp);border-radius:0 0 .25rem 0;animation:resize-tutorial-handle 1.2s cubic-bezier(.4,0,.2,1) infinite}
      .resize-tutorial-title{margin:0;color:var(--pip-text);font-size:1rem;line-height:1.45;font-weight:780}
      .resize-tutorial-copy{margin:.375rem 0 0;color:var(--pip-muted);font-size:.75rem;line-height:1.6}
      .resize-tutorial-dismiss{min-width:6rem;margin-top:.875rem;padding:.4375rem .875rem;border:1px solid color-mix(in srgb,var(--pip-exp) 45%,var(--pip-border));border-radius:.5rem;background:color-mix(in srgb,var(--pip-exp) 18%,var(--pip-surface));color:var(--pip-text);font-size:.75rem;font-weight:740;cursor:pointer}
      .resize-tutorial-dismiss:hover{background:color-mix(in srgb,var(--pip-exp) 28%,var(--pip-surface))}
      .resize-tutorial-dismiss:focus-visible{outline:2px solid var(--pip-exp);outline-offset:2px}
      .widget-actions{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.4375rem}.widget-actions button{min-width:0;min-height:2.25rem;padding:0 .6875rem;display:inline-flex;align-items:center;justify-content:center;gap:.4375rem;border:1px solid var(--pip-border);border-radius:.5rem;background:var(--pip-surface);color:var(--pip-text);font-size:.75rem;font-weight:740;white-space:nowrap;cursor:pointer;box-shadow:0 .0625rem .1875rem rgb(15 23 42 / ${dark ? '16%' : '4%'});transition:background-color var(--pip-motion) var(--pip-ease),border-color var(--pip-motion) var(--pip-ease),color var(--pip-motion) var(--pip-ease),box-shadow var(--pip-motion) var(--pip-ease),transform var(--pip-motion) var(--pip-ease)}.widget-actions button svg{width:.9375rem;height:.9375rem;display:block;flex:none}.widget-actions button.action-start,.widget-actions button.action-resume{border-color:color-mix(in srgb,var(--pip-success) 44%,var(--pip-border));color:var(--pip-success);background:var(--pip-success-soft)}.widget-actions button.action-pause{border-color:color-mix(in srgb,var(--pip-warning) 40%,var(--pip-border));color:var(--pip-warning);background:var(--pip-warning-soft)}.widget-actions button.danger{border-color:color-mix(in srgb,var(--pip-danger) 40%,var(--pip-border));color:var(--pip-danger);background:var(--pip-danger-soft)}.widget-actions button:hover:not(:disabled){border-color:currentColor;box-shadow:0 .1875rem .5rem rgb(15 23 42 / ${dark ? '24%' : '8%'});transform:translateY(-.0625rem)}.widget-actions button:active:not(:disabled){box-shadow:inset 0 0 0 1px color-mix(in srgb,currentColor 20%,transparent);transform:translateY(0)}.widget-actions button:focus-visible{outline:.1875rem solid color-mix(in srgb,var(--pip-success) 50%,transparent);outline-offset:.125rem}.widget-actions button.danger:focus-visible{outline-color:color-mix(in srgb,var(--pip-danger) 50%,transparent)}.widget-actions button:disabled{cursor:not-allowed;opacity:.44;box-shadow:none;transform:none}
      .widget[data-starting="true"] [data-action="primary"]{grid-column:1/-1}.widget[data-starting="true"] [data-action="end"]{display:none}
      .widget[data-layout="no-brand"],.widget[data-layout="no-resources"],.widget[data-layout="core"],.widget[data-layout="exp-only"],.widget[data-layout="micro"]{padding:.5rem;gap:.375rem}.widget[data-layout="no-brand"] .brand,.widget[data-layout="no-resources"] .brand{display:none}.widget[data-layout="no-brand"] .widget-header,.widget[data-layout="no-resources"] .widget-header{min-height:1.5rem}.widget[data-layout="no-brand"] .header-meta,.widget[data-layout="no-resources"] .header-meta{width:100%;justify-content:space-between}.widget[data-layout="no-brand"] .elapsed-label,.widget[data-layout="no-resources"] .elapsed-label{display:none}
      .widget[data-layout="no-brand"] .metric,.widget[data-layout="no-resources"] .metric{padding:.375rem .625rem}.widget[data-layout="no-brand"] .exp-metric,.widget[data-layout="no-resources"] .exp-metric{padding-block:.1875rem}.widget[data-layout="no-brand"] .metric-icon,.widget[data-layout="no-resources"] .metric-icon{width:1.375rem;height:1.375rem}.widget[data-layout="no-brand"] .metric-value,.widget[data-layout="no-resources"] .metric-value{min-height:1.25rem;margin-top:.1875rem;font-size:1.25rem}.widget[data-layout="no-brand"] .mesos-metric .metric-value,.widget[data-layout="no-resources"] .mesos-metric .metric-value{font-size:1.125rem}.widget[data-layout="no-brand"] .metric-meta,.widget[data-layout="no-resources"] .metric-meta{margin-top:.25rem}.widget[data-layout="no-brand"] .exp-metric .metric-meta,.widget[data-layout="no-resources"] .exp-metric .metric-meta{margin-top:.5rem}.widget[data-layout="no-brand"] .resources{padding:.3125rem .625rem}.widget[data-layout="no-brand"] .resource{padding-inline:.625rem}.widget[data-layout="no-brand"] .resource.hp{padding-left:0}.widget[data-layout="no-brand"] .resource.mp{padding-right:0}.widget[data-layout="no-brand"] .resource-name .metric-icon{width:1.125rem;height:1.125rem}.widget[data-layout="no-brand"] .bar{height:.25rem;margin-top:.25rem}
      .widget[data-layout="no-brand"] .exp-aux-item,.widget[data-layout="no-resources"] .exp-aux-item{padding-right:.25rem}.widget[data-layout="no-brand"] .exp-aux-item+.exp-aux-item,.widget[data-layout="no-resources"] .exp-aux-item+.exp-aux-item{padding-right:0;padding-left:.25rem}
      .widget[data-layout="no-resources"] .data-panel,.widget[data-layout="core"] .data-panel,.widget[data-layout="exp-only"] .data-panel,.widget[data-layout="micro"] .data-panel{grid-template-rows:minmax(0,1fr)}.widget[data-layout="no-resources"] .resources,.widget[data-layout="core"] .resources,.widget[data-layout="exp-only"] .resources,.widget[data-layout="micro"] .resources{display:none}
      .widget[data-layout="core"],.widget[data-layout="exp-only"],.widget[data-layout="micro"]{grid-template-rows:minmax(0,1fr) auto}.widget[data-layout="core"] .widget-header,.widget[data-layout="exp-only"] .widget-header,.widget[data-layout="micro"] .widget-header,.widget[data-layout="core"] .location,.widget[data-layout="exp-only"] .location,.widget[data-layout="micro"] .location{display:none}.widget[data-layout="core"] .metric,.widget[data-layout="exp-only"] .metric{padding:.5rem .625rem}.widget[data-layout="core"] .exp-metric,.widget[data-layout="exp-only"] .exp-metric{padding-block:.25rem}.widget[data-layout="core"] .metric-context,.widget[data-layout="exp-only"] .metric-context{font-size:.625rem}.widget[data-layout="core"] .metric-value,.widget[data-layout="exp-only"] .metric-value{font-size:1.25rem}.widget[data-layout="core"] .exp-metric .metric-value,.widget[data-layout="exp-only"] .exp-metric .metric-value{margin-top:.25rem}.widget[data-layout="core"] .exp-metric .metric-meta,.widget[data-layout="exp-only"] .exp-metric .metric-meta{margin-top:.5rem}.widget[data-layout="core"] .exp-aux-item,.widget[data-layout="exp-only"] .exp-aux-item{padding-right:.375rem}.widget[data-layout="core"] .exp-aux-item+.exp-aux-item,.widget[data-layout="exp-only"] .exp-aux-item+.exp-aux-item{padding-right:0;padding-left:.375rem}.widget[data-layout="core"] .mesos-value-row{gap:.1875rem}.widget[data-layout="core"] .mesos-net{gap:.0625rem;font-size:.5rem}.widget[data-layout="core"] .mesos-net strong{font-size:.5625rem}
      .widget[data-layout="core"] .widget-actions,.widget[data-layout="exp-only"] .widget-actions,.widget[data-layout="micro"] .widget-actions{gap:.375rem}.widget[data-layout="core"] .widget-actions button,.widget[data-layout="exp-only"] .widget-actions button,.widget[data-layout="micro"] .widget-actions button{width:100%;min-height:1.75rem;padding:0 .375rem;gap:.25rem;border-radius:.375rem;font-size:.625rem;box-shadow:none}.widget[data-layout="core"] .widget-actions button svg,.widget[data-layout="exp-only"] .widget-actions button svg,.widget[data-layout="micro"] .widget-actions button svg{width:.75rem;height:.75rem}
      .widget[data-layout="exp-only"] .core-metrics,.widget[data-layout="micro"] .core-metrics{grid-template-columns:minmax(0,1fr)}.widget[data-layout="exp-only"] .mesos-metric,.widget[data-layout="micro"] .mesos-metric{display:none}.widget[data-layout="exp-only"] .exp-metric{justify-content:center}.widget[data-layout="exp-only"] .widget-actions button{min-height:1.625rem}
      .widget[data-layout="micro"]{padding:.25rem;gap:.125rem}.widget[data-layout="micro"] .data-panel{box-shadow:none}.widget[data-layout="micro"] .exp-metric{padding:.25rem .4375rem;display:grid;grid-template-areas:"heading value" "meta meta";grid-template-columns:auto minmax(0,1fr);align-items:center;gap:.1875rem .375rem}.widget[data-layout="micro"] .metric-heading{grid-area:heading;justify-content:flex-start}.widget[data-layout="micro"] .metric-icon{width:1.1875rem;height:1.1875rem}.widget[data-layout="micro"] .metric-label{gap:0}.widget[data-layout="micro"] .metric-label>span:last-child,.widget[data-layout="micro"] .metric-context{display:none}.widget[data-layout="micro"] .metric-value{grid-area:value;min-height:1rem;margin:0;justify-self:end;gap:.25rem;font-size:1.0625rem}.widget[data-layout="micro"] .metric-gained{gap:0}.widget[data-layout="micro"] .metric-gained small{display:none}.widget[data-layout="micro"] .metric-gained strong{font-size:.5625rem}.widget[data-layout="micro"] .metric-meta{grid-area:meta;margin:0;gap:0}.widget[data-layout="micro"] .exp-aux-item{gap:.125rem;padding-right:.25rem}.widget[data-layout="micro"] .exp-aux-item+.exp-aux-item{padding-right:0;padding-left:.25rem}.widget[data-layout="micro"] .exp-aux-item dt{font-size:.5rem}.widget[data-layout="micro"] .exp-aux-item dd{font-size:.5625rem;line-height:1}.widget[data-layout="micro"] .widget-actions{gap:.25rem}.widget[data-layout="micro"] .widget-actions button{min-height:1.5rem;padding:0 .25rem;gap:.1875rem;border-radius:.3125rem;font-size:.5625rem}.widget[data-layout="micro"] .widget-actions button svg{width:.6875rem;height:.6875rem}
      @media(max-width:20rem){.status{max-width:6.25rem}.location-label{display:none}.metric-label{font-size:.6875rem}.metric-context{font-size:.625rem}}
      @media(max-width:18rem){.widget[data-shape="classic"]:not([data-layout="adaptive"]) .core-metrics{grid-template-columns:minmax(0,1fr)}.widget[data-shape="classic"]:not([data-layout="adaptive"]) .mesos-metric{display:none}.widget[data-shape="classic"]:not([data-layout="adaptive"]) .exp-metric{justify-content:center}}
      @media(max-width:10rem){.widget[data-layout="micro"] .widget-actions .button-icon{display:none}}
    `
    style.textContent += elapsedStyles + responsiveStyles + streamStyles + expTrendStyles + buffStyles + buffCompositionStyles + expChartStyles + experienceStyles + actionStyles + dailyPanelStyles + orderStyles
    pip.document.head.append(style)

    const root = pip.document.createElement('main')
    const composition = createBuffComposition(pip.document, overlay)
    root.className = 'widget'
    root.setAttribute('aria-label', productName)
    composition.stats.append(root)
    const streamHost = pip.document.createElement('div')
    streamHost.className = 'stream-widget'
    const streamViewport = pip.document.createElement('div')
    streamViewport.className = 'stream-widget-viewport'
    streamHost.append(streamViewport)
    let streamActive = false
    let panelActive = false
    const panelHost = pip.document.createElement('div')
    panelHost.className = 'daily-panel-host'
    let trendAnimator: ExpRateTrendAnimator | undefined
    const progress = new Map<HTMLElement, WidgetProgressAnimator>()
    let tutorialCleanup: (() => void) | null = null
    this.rendererCleanup = () => {
      tutorialCleanup?.()
      tutorialCleanup = null
      progress.forEach(bar => bar.destroy())
      trendAnimator?.destroy(); render(null, streamViewport)
      render(null, panelHost); panelHost.remove()
      const readoutHost = root.querySelector('[data-exp-readout-host]')
      if (readoutHost) render(null, readoutHost)
      const chartHost = root.querySelector('[data-exp-chart-host]')
      if (chartHost) render(null, chartHost)
      const identityHost = root.querySelector('[data-level]')
      if (identityHost) render(null, identityHost)
      streamHost.remove(); root.remove(); style.remove(); composition.dispose()
    }

    const icons = {
      brand: this.gameIcon(new URL('/favicon.png', window.location.href).href, Gamepad2, 'brand-mark'),
      clock: this.icon(Clock3),
      map: this.icon(MapPinned),
      exp: this.gameIcon(GAME_ICONS.exp, Sparkles),
      mesos: this.gameIcon(GAME_ICONS.mesos, Coins),
      hp: this.gameIcon(GAME_ICONS.hp, HeartPulse),
      mp: this.gameIcon(GAME_ICONS.mp, Droplets),
      pause: this.icon(Pause),
      play: this.icon(Play),
      end: this.icon(Square),
    }

    root.innerHTML = `
      <header class="widget-header">
        <div class="brand">${icons.brand}<div class="brand-copy"><strong>${productName}</strong></div></div>
        <div class="header-meta">
          <span class="elapsed widget-elapsed" data-widget-elapsed>${icons.clock}<span class="elapsed-label widget-elapsed-label" data-copy="elapsed">${labels.elapsed}</span><strong data-elapsed>00:00</strong></span>
          <span class="status status-idle" data-status-shell role="status" aria-atomic="true"><i class="status-dot"></i><span data-status></span><span data-status-short aria-hidden="true"></span></span>
        </div>
      </header>
      <div class="location">${icons.map}<span class="location-label" data-copy="map">${labels.map}</span><strong data-map></strong></div>
      <section class="data-panel" aria-label="${labels.metrics}">
        <div class="core-metrics">
          <article class="metric exp-metric" aria-label="${labels.exp}">
            <div class="metric-heading"><div class="metric-label">${icons.exp}<span data-copy="exp">${labels.exp}</span></div><div data-exp-readout-host></div><span class="metric-context" data-level></span></div>
            <div class="metric-value exp-value-row"><span class="exp-rate-group"><span data-exp-rate>--</span><span data-exp-trend class="exp-rate-trend"></span></span><span class="metric-gained"><small data-exp-gained-label data-copy="gained">${labels.gained}</small><strong data-exp-gained>--</strong></span></div>
            <div class="experience-footer" data-exp-footer><div class="experience-footer-eta" data-exp-eta-item><span data-copy="eta">${labels.eta}</span><b data-exp-eta>--</b></div><div data-exp-chart-host></div></div>
          </article>
          <article class="metric mesos-metric">
            <div class="metric-heading"><div class="metric-label">${icons.mesos}<span data-copy="mesos">${labels.mesos}</span></div></div>
            <div class="metric-value mesos-value-row"><span data-mesos-rate>--</span><span class="mesos-net" hidden><small data-copy="netChange">${labels.netChange}</small><strong data-mesos-net>--</strong></span></div>
            <div class="metric-meta mesos-meta"><span class="meta-item"><small data-copy="startBalance">${labels.startBalance}</small><strong data-mesos-start>--</strong></span><span class="meta-item mesos-balance-change" aria-label="${labels.balanceChange}"><small data-copy="balanceChange">${labels.balanceChange}</small><strong data-mesos-balance-change>--</strong></span></div>
          </article>
        </div>
        <div class="resources">
          <div class="resource hp">
            <div class="resource-heading"><span class="resource-name">${icons.hp}<span data-copy="hp">${labels.hp}</span></span><strong class="resource-percent" data-hp-percent>--</strong></div>
            <div class="bar"><i data-hp-bar style="--value:0%"></i></div>
          </div>
          <div class="resource mp">
            <div class="resource-heading"><span class="resource-name">${icons.mp}<span data-copy="mp">${labels.mp}</span></span><strong class="resource-percent" data-mp-percent>--</strong></div>
            <div class="bar"><i data-mp-bar style="--value:0%"></i></div>
          </div>
        </div>
      </section>
      <div class="buff-rail" data-buff-anchor hidden></div>
      <footer class="widget-actions">
        <button type="button" class="action-start" data-action="primary"><span class="button-icon" data-primary-play>${icons.play}</span><span class="button-icon" data-primary-pause hidden>${icons.pause}</span><span data-primary-label data-copy="start">${labels.start}</span></button>
        <button type="button" class="danger" data-action="end"><span class="button-icon">${icons.end}</span><span data-end-label data-copy="end">${labels.end}</span></button>
      </footer>
    `

    const query = <T extends Element>(selector: string): T => {
      const node = root.querySelector<T>(selector)
      if (!node) throw new Error(`画中画节点缺失: ${selector}`)
      return node
    }
    const nodes = {
      actions: query<HTMLElement>('.widget-actions'),
      statusShell: query<HTMLElement>('[data-status-shell]'),
      status: query<HTMLElement>('[data-status]'),
      elapsed: query<HTMLElement>('[data-elapsed]'),
      map: query<HTMLElement>('[data-map]'),
      location: query<HTMLElement>('.location'),
      coreMetrics: query<HTMLElement>('.core-metrics'),
      mesosMetric: query<HTMLElement>('.mesos-metric'),
      resources: query<HTMLElement>('.resources'),
      hpResource: query<HTMLElement>('.resource.hp'),
      mpResource: query<HTMLElement>('.resource.mp'),
      level: query<HTMLElement>('[data-level]'),
      expRate: query<HTMLElement>('[data-exp-rate]'),
      expGained: query<HTMLElement>('[data-exp-gained]'),
      expGainedLabel: query<HTMLElement>('[data-exp-gained-label]'),
      expEta: query<HTMLElement>('[data-exp-eta]'),
      mesosRate: query<HTMLElement>('[data-mesos-rate]'),
      mesosNet: query<HTMLElement>('[data-mesos-net]'),
      mesosStart: query<HTMLElement>('[data-mesos-start]'),
      mesosBalanceChange: query<HTMLElement>('[data-mesos-balance-change]'),
      hpPercent: query<HTMLElement>('[data-hp-percent]'),
      hpBar: query<HTMLElement>('[data-hp-bar]'),
      mpPercent: query<HTMLElement>('[data-mp-percent]'),
      mpBar: query<HTMLElement>('[data-mp-bar]'),
      primary: query<HTMLButtonElement>('[data-action="primary"]'),
      primaryPlay: query<HTMLElement>('[data-primary-play]'),
      primaryPause: query<HTMLElement>('[data-primary-pause]'),
      primaryLabel: query<HTMLElement>('[data-primary-label]'),
      end: query<HTMLButtonElement>('[data-action="end"]'),
      endLabel: query<HTMLElement>('[data-end-label]'),
    }
    root.querySelectorAll('img').forEach((image) => image.addEventListener('error', () => image.remove(), { once: true }))

    const groups = [
      { node: query<HTMLElement>('.brand'), priority: 5 },
      { node: nodes.location, priority: 5 },
      { node: nodes.statusShell, priority: 2 },
      { node: query<HTMLElement>('.metric-gained'), priority: 1 },
      { node: nodes.mesosMetric, priority: 3 },
      { node: nodes.resources, priority: 4 },
      { node: query<HTMLElement>('[data-buff-anchor]'), priority: 1 },
    ]
    const valueRows = [...root.querySelectorAll<HTMLElement>('.mesos-value-row, .mesos-meta, .exp-aux')]
    const shortStatus = query<HTMLElement>('[data-status-short]')
    let layoutFrame: number | null = null
    let layoutReady = false
    let layoutDirty = true
    let lastLayoutContent = ''
    let lastLayoutLevel = '--'
    let lastFitContext = ''
    const applyDetail = (detail: number) => {
      root.dataset.detail = String(detail)
      for (const group of groups) group.node.toggleAttribute('data-layout-hidden', group.priority > detail)
    }
    const renderLayout = () => {
      layoutFrame = null
      if (this.pipWindow !== pip || pip.closed) return
      if (streamActive || panelActive) return
      layoutDirty = false
      const width = composition.stats.clientWidth || (Number.isFinite(pip.innerWidth) && pip.innerWidth > 0 ? pip.innerWidth : 360)
      const height = composition.stats.clientHeight || (Number.isFinite(pip.innerHeight) && pip.innerHeight > 0 ? pip.innerHeight : 280)
      const fitContext = `${width}:${height}:${pip.document.documentElement.dataset.pipSkin}:${locale}:${rateUnit}:${lastLayoutContent}:${enabledBuffCount(overlay())}`
      // Live values and temporary readout states keep their established slots.
      if (fitContext === lastFitContext) return
      resetPipScale(root)
      lastFitContext = fitContext
      const shape = resolvePipShape(width, height)
      root.dataset.shape = shape
      root.dataset.layout = shape === 'classic' ? width <= 220 && height <= 160 ? 'micro' : resolvePipLayout(width, height) : 'adaptive'
      root.dataset.iconActions = String(shape === 'strip' || width < 200 || root.dataset.starting === 'true')
      delete root.dataset.compactStatus
      for (const row of valueRows) row.removeAttribute('data-wrap')
      for (const group of groups) group.node.removeAttribute('data-layout-hidden')
      delete root.dataset.detail
      delete root.dataset.trendHidden
      root.dataset.elapsedMode = 'full'
      const fitsElapsed = () => fitWidgetElapsed(root, pipContentFits)
      const combinedBuffs = enabledBuffCount(overlay()) > 0
      const comfortableClassic = shape === 'classic' && width >= 236 && height >= 144
      if (shape === 'classic') {
        if (nodes.status.clientWidth && nodes.status.scrollWidth > nodes.status.clientWidth + 1) root.dataset.compactStatus = 'true'
        for (const row of valueRows) {
          if ([row, ...row.querySelectorAll<HTMLElement>('*')].some((node) => node.clientWidth && node.scrollWidth > node.clientWidth + 1)) {
            row.setAttribute('data-wrap', '')
          }
        }
        // Try the complete composition first, then retire secondary detail
        // before scaling the essential experience readings.
        if (comfortableClassic) {
          if (!fitsElapsed()) root.dataset.trendHidden = 'true'
          if (fitsElapsed()) return
        }
        if (fitsElapsed()) return
      }
      selectPipDetail((detail) => {
        applyDetail(detail)
        return fitsElapsed()
      }, 5)
      // The comparison is the last optional metric to go, after net gain/details.
      if (!pipContentFits(root)) root.dataset.trendHidden = 'true'
      if (!pipContentFits(root)) root.dataset.elapsedMode = 'hidden'
      fitPipScale(root, pipContentFits, combinedBuffs ? 1.15 : comfortableClassic ? 4 : Infinity)
    }
    const syncLayout = () => {
      layoutFrame = null
      if (!isCurrent() || contentFailures >= 3 || layoutFailures >= 3) return
      try { renderLayout(); layoutFailures = 0 } catch (error) { contentFailed('layout', error) }
    }
    const scheduleLayout = () => {
      layoutDirty = true
      if (!layoutReady || layoutFrame !== null) return
      if (typeof pip.requestAnimationFrame === 'function') layoutFrame = pip.requestAnimationFrame(syncLayout)
      else syncLayout()
    }
    const resized = () => {
      tutorialCleanup?.(); tutorialCleanup = null
      composition.invalidateLayout()
      try { composition.update(streamActive || panelActive) } catch (error) { contentFailed('layout', error) }
      lastFitContext = ''; scheduleLayout()
    }
    pip.addEventListener('resize', resized)
    const fontsChanged = () => { lastFitContext = ''; scheduleLayout() }
    void pip.document.fonts?.ready.then(fontsChanged)
    pip.document.fonts?.addEventListener('loadingdone', fontsChanged)
    this.layoutCleanup = () => {
      layoutReady = false
      pip.removeEventListener?.('resize', resized)
      pip.document.fonts?.removeEventListener('loadingdone', fontsChanged)
      if (layoutFrame !== null) pip.cancelAnimationFrame?.(layoutFrame)
      layoutFrame = null
    }

    let pendingCommand: 'primary' | 'end' | null = null
    trendAnimator = new ExpRateTrendAnimator(query<HTMLElement>('[data-exp-trend]'))
    let progressContext = ''
    let progressMotion = false
    const setProgress = (node: HTMLElement, ratio: number) => {
      node.style.setProperty('--value', `${Math.round(ratio * 100)}%`)
      let bar = progress.get(node)
      if (!bar) { bar = new WidgetProgressAnimator(node); progress.set(node, bar) }
      bar.update(ratio, progressContext, progressMotion)
    }
    const setText = (node: HTMLElement, value: string) => {
      if (node.textContent !== value) {
        node.textContent = value
        layoutDirty = true
      }
    }
    const setSignedTone = (node: HTMLElement, value: number | null | undefined) => {
      node.classList.toggle('signed-positive', typeof value === 'number' && Number.isFinite(value) && value > 0)
      node.classList.toggle('signed-negative', typeof value === 'number' && Number.isFinite(value) && value < 0)
    }
    let lastControls = ''
    let lastContent = ''
    let lastModel: StreamHudData | undefined
    let lastBuffKey = ''
    let displayBuffs: BuffSnapshot | null = null
    const updateControls = (input: PipData) => {
      const key = JSON.stringify([input.locale ?? locale, input.session?.status, input.canStart,
        input.isStarting, input.isEnding, input.isPreparingForReload, pendingCommand])
      if (key === lastControls) return
      const labels = getLabels(input.locale ?? locale)
      const { session, canStart, isStarting, isEnding, isPreparingForReload } = input
      const ending = isEnding || pendingCommand === 'end'
      const starting = isStarting && !ending
      root.dataset.starting = String(starting)
      root.setAttribute('aria-busy', String(starting))
      const canBegin = !ending && (!session || session.status === 'idle' || session.status === 'ended')
      const canToggle = !ending && (session?.status === 'running' || session?.status === 'paused')
      const canEnd = Boolean(!ending && session && session.status !== 'ended')
      const primaryAction = canBegin ? 'start' : session?.status === 'paused' ? 'resume' : 'pause'
      const primaryLabel = starting ? labels.starting : canBegin ? labels.start : session?.status === 'paused' ? labels.resume : labels.pause
      const endLabel = ending ? labels.ending : labels.end

      // Both templates share the same controls and event handlers.
      nodes.primary.className = `action-${primaryAction}`
      nodes.primary.disabled = Boolean(isPreparingForReload) || starting || pendingCommand !== null || !(canBegin ? canStart : canToggle)
      nodes.primary.setAttribute('aria-busy', String(starting || pendingCommand === 'primary'))
      nodes.primary.setAttribute('aria-label', primaryLabel)
      nodes.primary.title = primaryLabel
      nodes.primaryPlay.hidden = primaryAction === 'pause'
      nodes.primaryPause.hidden = primaryAction !== 'pause'
      setText(nodes.primaryLabel, primaryLabel)
      nodes.end.disabled = Boolean(isPreparingForReload) || pendingCommand !== null || !canEnd
      nodes.end.setAttribute('aria-busy', String(ending))
      nodes.end.setAttribute('aria-label', endLabel)
      nodes.end.title = endLabel
      setText(nodes.endLabel, endLabel)
      lastControls = key
    }
    const renderContent = (input: PipData, nextSample: StreamHudData | undefined, nextOverlay: BuffSnapshot | null, force: boolean): boolean => {
      const skin = normalizePipSkin(input.skin)
      const nextDark = (input.theme ?? document.documentElement.dataset.theme) === 'dark'
      const nextLocale = input.locale ?? nextOverlay?.locale ?? locale
      const snapshot = buildWidgetSnapshot({ ...input, isEnding: input.isEnding || pendingCommand === 'end' }, nextLocale, nextDark ? 'dark' : 'light')
      const model = reuseWidgetData(lastModel, nextSample ?? snapshot.data)
      const nextBuffKey = buffPresentationKey(nextOverlay)
      const buffs = nextBuffKey === lastBuffKey ? displayBuffs : nextOverlay
      const contentKey = JSON.stringify([skin, snapshot.theme, nextLocale, snapshot.options, model, snapshot.mesosNetTone, snapshot.mesosBalanceTone, snapshot.unresolvedExp])
      if (!force && contentKey === lastContent && nextBuffKey === lastBuffKey) return false
      const complete = () => {
        lastContent = layoutFailures ? '' : contentKey
        lastModel = model; lastBuffKey = nextBuffKey; displayBuffs = buffs
        return true
      }
      if (composition.update(isStreamSkin(skin) || isDailyPanelSkin(skin), buffs)) layoutDirty = true
      if (nextDark !== dark) {
        dark = nextDark
        const palette = defaultPipColors(dark)
        const properties = { bg: palette.bg, surface: palette.surface, subtle: palette.subtle, text: palette.text, muted: palette.muted,
          border: palette.border, danger: palette.danger, 'danger-soft': palette.dangerSoft, success: palette.green,
          'success-soft': palette.greenSoft, exp: palette.exp, mesos: palette.mesos, warning: palette.amber,
          'warning-soft': palette.amberSoft, hp: palette.hp, mp: palette.mp }
        for (const [name, value] of Object.entries(properties)) pip.document.documentElement.style.setProperty(`--pip-${name}`, value)
        pip.document.documentElement.dataset.theme = dark ? 'dark' : 'light'
        pip.document.documentElement.style.colorScheme = dark ? 'dark' : 'light'
        layoutDirty = true
      }
      const nextUnit = normalizeRateUnit(input.rateUnit)
      if (nextLocale !== locale || nextUnit !== rateUnit) {
        rateUnit = nextUnit
        locale = nextLocale
        labels = unitLabels()
        pip.document.documentElement.lang = locale
        const name = widgetCopy(locale, 'Maple Log', '枫记')
        pip.document.title = name
        root.setAttribute('aria-label', name)
        setText(query<HTMLElement>('.brand-copy strong'), name)
        root.querySelectorAll<HTMLElement>('[data-copy]').forEach(node => {
          if (!nodes.actions.contains(node)) setText(node, labels[node.dataset.copy as keyof PipLabels])
        })
        query('.data-panel').setAttribute('aria-label', labels.metrics)
        query('.exp-metric').setAttribute('aria-label', labels.exp)
        query('.mesos-balance-change').setAttribute('aria-label', labels.balanceChange)
        layoutDirty = true
      }
      composition.updateStats(model.expRate, labels.exp)
      const normalizedSkin = snapshot.skin
      composition.theme(normalizedSkin, isDailyPanelSkin(normalizedSkin) ? dailyPanelBackground(normalizedSkin, snapshot.theme) : resolvePipBackground(isStreamSkin(normalizedSkin) ? snapshot.options.background : snapshot.options.dailyBackground ?? 'transparent'))
      progressContext = `${model.sessionId}:${normalizedSkin}:${locale}:${rateUnit}`
      progressMotion = model.status === 'running'
      const selectedSkin = normalizedSkin === 'default' ? undefined : normalizedSkin
      if (pip.document.documentElement.dataset.pipSkin !== selectedSkin) {
        if (selectedSkin) pip.document.documentElement.dataset.pipSkin = selectedSkin
        else delete pip.document.documentElement.dataset.pipSkin
        layoutDirty = true
      }
      const enabled = model.enabled
      const starting = input.isStarting && !input.isEnding && pendingCommand !== 'end'
      const statusKey = model.status

      if (panelActive && !isDailyPanelSkin(normalizedSkin)) {
        // Reclaim the shared command buttons before unmounting their Vue slot.
        root.append(nodes.actions)
        render(null, panelHost)
        panelHost.remove()
        composition.stats.append(root)
        panelActive = false
        layoutDirty = true
      }
      if (isDailyPanelSkin(normalizedSkin)) {
        progress.forEach(bar => bar.reset())
        trendAnimator.update(null, normalizedSkin, false)
        if (streamActive) {
          render(null, streamViewport)
          streamHost.remove()
          streamActive = false
        }
        if (!panelActive) {
          render(null, query<HTMLElement>('[data-exp-readout-host]'))
          render(null, query<HTMLElement>('[data-exp-chart-host]'))
          root.remove()
          composition.stats.append(panelHost)
          panelActive = true
        }
        const background = dailyPanelBackground(normalizedSkin, snapshot.theme)
        pip.document.documentElement.style.background = background
        pip.document.body.style.background = background
        render(h(dailyPanelComponents[normalizedSkin], { data: model, locale, theme: snapshot.theme, buffs }, {
          actions: () => h('div', { class: 'daily-action-host', ref: (element: Element | ComponentPublicInstance | null) => {
            const host = element as HTMLElement | null
            if (host && nodes.actions.parentNode !== host) host.append(nodes.actions)
          } }),
        }), panelHost)
        return complete()
      }

      if (isStreamSkin(normalizedSkin)) {
        progress.forEach(bar => bar.reset())
        trendAnimator.update(null, normalizedSkin, false)
        if (!streamActive) {
          render(null, query<HTMLElement>('[data-exp-readout-host]'))
          render(null, query<HTMLElement>('[data-exp-chart-host]'))
          root.remove()
          streamHost.append(nodes.actions)
          composition.stats.append(streamHost)
          streamActive = true
        }
        const options = snapshot.options
        streamHost.dataset.streamSkin = normalizedSkin
        streamHost.style.setProperty('--stream-canvas', resolvePipBackground(options.background))
        pip.document.documentElement.style.background = 'transparent'
        pip.document.body.style.background = 'transparent'
        render(h(StreamHud, { skin: normalizedSkin, data: model, locale, options, buffs, maxExpansion: enabledBuffCount(buffs) > 0 ? 1.15 : undefined }), streamViewport)
        return complete()
      }
      if (streamActive) {
        render(null, streamViewport)
        streamHost.remove()
        root.append(nodes.actions)
        composition.stats.append(root)
        streamActive = false
        pip.document.documentElement.style.background = ''
        pip.document.body.style.background = ''
        layoutDirty = true
      }
      // Temporary unreadable levels keep their established slot; a newly
      // recognized level or profession must invalidate the layout cache.
      if (model.level !== '--') lastLayoutLevel = model.level
      const layoutContent = JSON.stringify([model.sessionId, enabled.mapName, enabled.mesos, enabled.hp, enabled.mp,
        starting, elapsedLayoutWidth(model.elapsed), lastLayoutLevel])
      const dailyBackground = snapshot.options.dailyBackground ?? 'transparent'
      root.style.background = dailyBackground ? resolvePipBackground(dailyBackground) : ''
      pip.document.documentElement.style.background = dailyBackground ? 'transparent' : ''
      pip.document.body.style.background = dailyBackground ? 'transparent' : ''
      if (layoutContent !== lastLayoutContent) {
        lastLayoutContent = layoutContent
        layoutDirty = true
      }

      nodes.location.hidden = !enabled.mapName
      nodes.mesosMetric.hidden = !enabled.mesos
      nodes.coreMetrics.classList.toggle('is-single-metric', !enabled.mesos)
      nodes.hpResource.hidden = !enabled.hp
      nodes.mpResource.hidden = !enabled.mp
      nodes.resources.hidden = !enabled.hp && !enabled.mp
      nodes.resources.classList.toggle('is-single-resource', enabled.hp !== enabled.mp)

      nodes.statusShell.className = `status status-${statusKey}`
      const visibleStatus = starting ? labels.starting : model.statusText
      setText(nodes.status, visibleStatus)
      nodes.statusShell.title = visibleStatus
      nodes.statusShell.setAttribute('aria-label', visibleStatus)
      setText(shortStatus, model.statusText)
      setText(nodes.elapsed, model.elapsed)
      nodes.elapsed.style.minWidth = `${elapsedLayoutWidth(model.elapsed)}ch`
      nodes.elapsed.parentElement!.title = `${labels.elapsed} ${model.elapsed}`
      nodes.elapsed.parentElement!.setAttribute('aria-label', `${labels.elapsed} ${model.elapsed}`)
      setText(nodes.map, model.map)
      nodes.map.title = model.map
      render(h(WidgetPlayerIdentity, { level: model.level, jobName: model.jobName, levelLabel: labels.level }), nodes.level)
      setText(nodes.expRate, model.expRate)
      trendAnimator.update(model.expRateTrend, `${model.sessionId}:${normalizedSkin}:${locale}:${rateUnit}`, model.status === 'running')
      nodes.expRate.title = model.expReadout?.description ?? ''
      render(h(ExpReadoutStatus, { model: model.expReadout, iconOnly: true, locale, context: model.sessionId ?? '' }), query<HTMLElement>('[data-exp-readout-host]'))
      setText(nodes.expGainedLabel, model.expGainedLabel)
      setText(nodes.expGained, model.expGained)
      setText(nodes.expEta, model.eta)
      render(h(ExpRateChart, { model: model.expChart, locale, rateUnit, widget: true }), query<HTMLElement>('[data-exp-chart-host]'))
      setText(nodes.mesosRate, model.mesosRate)
      setText(nodes.mesosNet, model.mesosNet)
      setSignedTone(nodes.mesosNet, snapshot.mesosNetTone)
      setText(nodes.mesosStart, model.mesosStart)
      setText(nodes.mesosBalanceChange, model.mesosBalanceChange)
      setSignedTone(nodes.mesosBalanceChange, snapshot.mesosBalanceTone)
      setText(nodes.hpPercent, model.hpPercent)
      setProgress(nodes.hpBar, model.hpRatio)
      setText(nodes.mpPercent, model.mpPercent)
      setProgress(nodes.mpBar, model.mpRatio)

      if (layoutDirty) scheduleLayout()
      return complete()
    }
    let contentFailures = 0
    let layoutFailures = 0
    let retryAfter = 0
    let latestInput: PipData | undefined
    const isCurrent = () => this.generation === generation && this.pipWindow === pip && !pip.closed
    const notice = pip.document.createElement('div')
    notice.className = 'widget-render-error'
    notice.setAttribute('role', 'status')
    notice.setAttribute('aria-atomic', 'true')
    notice.hidden = true
    nodes.actions.append(notice)
    const reportFailure = (phase: WidgetFailure['phase'], error: unknown) => {
      performanceDiagnostics.widgetFailure({ phase,
        errorKind: error instanceof Error && ['Error', 'TypeError', 'RangeError', 'DOMException'].includes(error.name) ? error.name : 'Error',
        skin: normalizePipSkin(latestInput?.skin), durationMs: latestInput?.session?.activeDurationMs ?? 0,
        sessionStatus: latestInput?.session?.status ?? 'idle', isEnding: latestInput?.isEnding ?? false,
        isStarting: latestInput?.isStarting ?? false, reloadBlocked: latestInput?.isPreparingForReload ?? false,
        pendingCommand, contentFailures: Math.max(contentFailures, layoutFailures),
      })
    }
    const contentFailed = (phase: 'content' | 'layout', error: unknown) => {
      if (phase === 'layout') layoutFailures++
      else contentFailures++
      lastFitContext = ''
      layoutDirty = true
      retryAfter = performance.now() + 1_000
      // A failed frame must not become the cache entry for a successful render.
      lastContent = ''
      reportFailure(phase, error)
      if (contentFailures < 3 && layoutFailures < 3) return
      notice.textContent = widgetCopy(locale, 'Display unavailable. Reopen the widget.', '显示异常，请重新打开小组件。')
      notice.hidden = false
      composition.stats.classList.add('pip-display-failed')
      composition.stats.append(nodes.actions)
    }
    const update = (force = true, reason: WidgetRefreshReason = 'command') => {
      if (!isCurrent()) return
      let input: PipData
      try {
        input = getData()
        latestInput = input
        performanceDiagnostics.setWidgetContext({ open: true, mode: this.windowMode ?? 'preview', skin: normalizePipSkin(input.skin), mainVisibility: document.visibilityState })
        updateControls(input)
      } catch (error) { reportFailure('controls', error); return }
      if (contentFailures >= 3 || layoutFailures >= 3) return
      // Bound retries to once per second even when snapshot notifications arrive faster.
      if ((contentFailures > 0 || layoutFailures > 0) && performance.now() < retryAfter) return
      try {
        const nextOverlay = overlay()
        const nextSample = sample?.()
        const started = performance.now()
        const rendered = renderContent(input, nextSample, nextOverlay, force)
        if (rendered) performanceDiagnostics.measure('widget-render', performance.now() - started)
        performanceDiagnostics.widgetRefresh(reason, rendered)
        contentFailures = 0
      } catch (error) { contentFailed('content', error) }
    }
    this.refreshData = update

    const invoke = (kind: 'primary' | 'end', callback: () => void | Promise<void>) => {
      if (!isCurrent() || pendingCommand !== null) return
      pendingCommand = kind
      const complete = () => {
        pendingCommand = null
        if (isCurrent()) update()
      }
      try {
        const result = callback()
        if (result && typeof result.then === 'function') {
          // Install release handlers before rendering can fail. Both handlers consume rejection.
          void Promise.resolve(result).then(complete, error => {
            if (isCurrent()) reportFailure('command', error)
            complete()
          })
          update()
        } else complete()
      } catch (error) {
        if (isCurrent()) reportFailure('command', error)
        complete()
      }
    }

    nodes.primary.addEventListener('click', () => {
      if (!isCurrent() || nodes.primary.disabled || pendingCommand !== null) return
      const { session, canStart, isEnding, isPreparingForReload } = getData()
      if (isEnding || isPreparingForReload) return
      if (!session || session.status === 'idle' || session.status === 'ended') {
        if (canStart) invoke('primary', callbacks.start)
        return
      }
      if (session.status === 'paused') invoke('primary', callbacks.resume)
      else if (session.status === 'running') invoke('primary', callbacks.pause)
    })
    nodes.end.addEventListener('click', () => {
      if (!isCurrent() || nodes.end.disabled || pendingCommand !== null || getData().isPreparingForReload) return
      invoke('end', callbacks.end)
    })
    update(true, 'initial')
    layoutReady = true
    syncLayout()
    if (showResizeTutorial && typeof pip.requestAnimationFrame === 'function' && !readPipResizeTutorialSeen()) {
      const tutorial = pip.document.createElement('section')
      tutorial.className = 'resize-tutorial'
      tutorial.setAttribute('role', 'dialog')
      tutorial.setAttribute('aria-label', widgetCopy(locale, 'Resize widget', '调整小组件大小'))
      tutorial.innerHTML = `
        <div class="resize-tutorial-card">
          <div class="resize-tutorial-visual" aria-hidden="true">
            <div class="resize-tutorial-frame"></div><span class="resize-tutorial-arrow">↘</span><span class="resize-tutorial-handle"></span>
          </div>
          <h2 class="resize-tutorial-title">${widgetCopy(locale, 'Drag the window edge to resize', '拖动窗口边缘调整大小')}</h2>
          <p class="resize-tutorial-copy">${widgetCopy(locale, 'The widget adapts automatically and remembers its size.', '小组件会自动适应，尺寸会记住')}</p>
          <button type="button" class="resize-tutorial-dismiss">${widgetCopy(locale, 'Got it', '知道了')}</button>
        </div>`
      pip.document.body.append(tutorial)
      markPipResizeTutorialSeen()
      const dismissButton = tutorial.querySelector<HTMLButtonElement>('.resize-tutorial-dismiss')!
      const dismiss = () => {
        tutorial.remove()
        dismissButton.removeEventListener('click', dismiss)
        if (tutorialCleanup === dismiss) tutorialCleanup = null
      }
      tutorialCleanup = dismiss
      dismissButton.addEventListener('click', dismiss)
      tutorial.addEventListener('animationend', event => {
        if (event.target === tutorial) dismiss()
      })
      dismissButton.focus()
    }
    const timerWindow = typeof pip.setInterval === 'function' && typeof pip.clearInterval === 'function' ? pip : window
    this.timerWindow = timerWindow
    this.timer = timerWindow.setInterval(() => update(false, 'timer'), 1_000)
    if (callbacks.heartbeat) {
      const heartbeat = () => {
        if (!isCurrent()) return
        const started = performance.now()
        try { pulseBuffOverlay() } catch (error) { reportFailure('heartbeat', error) }
        try { callbacks.heartbeat?.() } catch (error) { reportFailure('heartbeat', error) }
        performanceDiagnostics.measure('widget-heartbeat', performance.now() - started)
      }
      callbacks.heartbeat()
      this.heartbeatTimer = timerWindow.setInterval(heartbeat, 250)
    }
  }

  close(): void {
    this.cancelPendingRefresh()
    this.generation += 1
    this.cancelOpening?.()
    const timerWindow = this.timerWindow ?? window
    const timer = this.timer
    const heartbeatTimer = this.heartbeatTimer
    const cleanups = [this.pageCleanup, this.openerCleanup, this.loadingCleanup, this.sizeCleanup, this.layoutCleanup, this.rendererCleanup]
    this.sizeCleanup = null
    this.pageCleanup = null
    this.openerCleanup = null
    this.loadingCleanup = null
    this.rendererCleanup = null
    this.layoutCleanup = null
    this.timer = null
    this.heartbeatTimer = null
    this.timerWindow = null
    this.pipWindow = null
    this.windowMode = null
    this.refreshData = null
    performanceDiagnostics.closeWidget()
    // A partially initialized or already closed window must not prevent teardown.
    for (const cleanup of [
      () => { if (timer !== null) timerWindow.clearInterval(timer) },
      () => { if (heartbeatTimer !== null) timerWindow.clearInterval(heartbeatTimer) },
      ...cleanups,
    ]) {
      try { cleanup?.() } catch { /* Continue releasing the remaining resources. */ }
    }
  }

  refresh(): void {
    this.cancelPendingRefresh()
    this.refreshData?.(true, 'explicit')
  }

  /** Publish once in the PiP's own frame, even when the main tab is in the background. */
  requestRefresh(): void {
    if (!this.refreshData || !this.pipWindow || this.pipWindow.closed || this.pendingRefresh) return
    const owner = typeof this.pipWindow.requestAnimationFrame === 'function' ? this.pipWindow : window
    const frame = typeof owner.requestAnimationFrame === 'function'
    const generation = this.generation
    const run = () => {
      this.pendingRefresh = null
      if (generation === this.generation) this.refreshData?.(false, 'snapshot')
    }
    const id = frame ? owner.requestAnimationFrame(run) : owner.setTimeout(run, 0)
    this.pendingRefresh = { owner, id, frame }
  }

  private cancelPendingRefresh(): void {
    const pending = this.pendingRefresh
    if (!pending) return
    this.pendingRefresh = null
    if (pending.frame) pending.owner.cancelAnimationFrame(pending.id)
    else pending.owner.clearTimeout(pending.id)
  }

  private icon(component: Component): string {
    const host = document.createElement('span')
    render(h(component, { 'aria-hidden': 'true', strokeWidth: 2 }), host)
    const markup = host.innerHTML
    render(null, host)
    return markup
  }

  private gameIcon(src: string, fallback: Component, className = 'metric-icon'): string {
    return `<span class="${className}">${this.icon(fallback)}<img src="${src}" alt="" aria-hidden="true"></span>`
  }
}

export const pipService = new PipService()
